import { ref, computed, h, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { SettingOutlined } from '@ant-design/icons-vue'
import { Dropdown, Menu, Checkbox } from 'ant-design-vue'
import { TABLE_SCROLL_Y, useTableScroll } from '@/mixins/useTableScroll'

const STORAGE_KEY = 'settings-table-columns'

export function useTableColumns(initialColumns, options = {}) {
    const allColumns = ref(initialColumns)
    const settingsWidth = options.settingsWidth || 40;
    const selectionWidth = options.selectionWidth || 0;
    const scrollYSize = options.scrollYSize || TABLE_SCROLL_Y.full;
    const tableKey = options.tableKey;
    const useSettingsAsHeader = options.hideSettings || false;

    const visibleColumns = computed(() => allColumns.value.filter(col => col.visible));
    const showScrollBar = ref(window.innerWidth <= 1150)
    const settingsDropdownOpen = ref(false)
    const tableWidth = ref(0)
    let tableResizeObserver = null

    const onResize = () => {
        showScrollBar.value = window.innerWidth <= 1150
        updateTableWidth()
    }

    const getTableElement = () => tableRef.value?.$el ?? tableRef.value

    const updateTableWidth = async () => {
        await nextTick()

        const tableEl = getTableElement()
        if (!tableEl) return

        tableWidth.value = tableEl.clientWidth || 0
    }

    const parseColumnWidth = (width, percentageBase = 0) => {
        if (typeof width === 'number') {
            return width;
        }

        if (typeof width === 'string' && width.endsWith('%')) {
            return percentageBase * (Number.parseFloat(width) / 100);
        }

        return 0;
    }

    const minDataColumnsWidth = computed(() => {
        const percentageBase = options.width || tableWidth.value || 0;

        return visibleColumns.value.reduce((sum, col) => sum + parseColumnWidth(col.width, percentageBase), 0);
    })

    const dataColumnsWidth = computed(() => {
        const minWidth = options.width || minDataColumnsWidth.value;
        const availableWidth = tableWidth.value - settingsWidth - selectionWidth;

        return Math.max(minWidth, availableWidth, 0);
    })

    const normalizedVisibleColumns = computed(() => {
        if (!dataColumnsWidth.value) {
            return visibleColumns.value;
        }

        const baseWidth = visibleColumns.value.reduce((sum, col) => sum + parseColumnWidth(col.width, dataColumnsWidth.value), 0);
        const widthScale = baseWidth > 0 ? Math.max(dataColumnsWidth.value / baseWidth, 1) : 1;
        const fallbackWidth = visibleColumns.value.length
            ? Math.floor(dataColumnsWidth.value / visibleColumns.value.length)
            : dataColumnsWidth.value;

        return visibleColumns.value.map((col) => {
            const columnWidth = parseColumnWidth(col.width, dataColumnsWidth.value);

            return {
                ...col,
                width: columnWidth ? Math.round(columnWidth * widthScale) : fallbackWidth,
            };
        });
    });

    const scrollX = computed(() => {
        if (dataColumnsWidth.value) {
            return dataColumnsWidth.value + settingsWidth + selectionWidth;
        }
        const totalWidth = visibleColumns.value.reduce((sum, col) => sum + (parseColumnWidth(col.width, dataColumnsWidth.value) || 0), 0)
        return totalWidth + settingsWidth + selectionWidth + 50
    })

    const scrollY = computed(() => {
        return showScrollBar.value ? undefined : scrollYSize;
    });

    const {
        tableRef,
        tableScroll,
        updateTableYScroll,
    } = useTableScroll({
        scrollX,
        dataSource: options.dataSource,
        scrollY: scrollYSize,
    });

    const toggleColumn = (col) => {
        const visibleCount = visibleColumns.value.length
        if (col.visible && visibleCount === 1) return
        col.visible = !col.visible
    }

    const isLastVisible = (col) => col.visible && visibleColumns.value.length === 1

    const fixedColumnStyle = (width) => ({
        '--table-settings-column-width': `${width}px`,
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        paddingLeft: 0,
        paddingRight: 0,
        textAlign: 'center',
    })

    const renderSettingsDropdown = (placement = 'bottomRight') => h(
        Dropdown,
        {
            trigger: ['click'],
            placement,
            open: settingsDropdownOpen.value,
            onOpenChange: (open) => {
                settingsDropdownOpen.value = open
            },
            getPopupContainer: () => document.body
        },
        {
            default: () => h(SettingOutlined, {
                style: { cursor: 'pointer', color: '#999' },
                onClick: (e) => e.stopPropagation(),
            }),
            overlay: () => h(
                Menu,
                { style: { maxHeight: '400px', overflowY: 'auto', minWidth: '200px' } },
                () => allColumns.value.map(col => h(
                    Menu.Item,
                    { key: col.dataIndex, disabled: isLastVisible(col), style: { padding: 0 } },
                    () => h(
                        Checkbox,
                        {
                            checked: col.visible,
                            disabled: isLastVisible(col),
                            style: { display: 'flex', width: '100%', padding: '5px 12px' },
                            onChange: () => toggleColumn(col),
                            onClick: (e) => e.stopPropagation()
                        },
                        () => col.title
                    )
                ))
            )
        }
    );

    const settingsColumn = computed(() => ({
        title: () => renderSettingsDropdown('bottomRight'),
        dataIndex: 'settings',
        width: settingsWidth,
        align: 'center',
        className: 'table-settings-column',
        customHeaderCell: () => ({
            class: 'table-settings-column',
            style: fixedColumnStyle(settingsWidth),
        }),
        customCell: () => ({
            class: 'table-settings-column',
            style: fixedColumnStyle(settingsWidth),
        }),
    }))

    const columns = computed(() => {
        return useSettingsAsHeader
            ? normalizedVisibleColumns.value
            : [settingsColumn.value, ...normalizedVisibleColumns.value]
    });

    const resetColumns = () => {
        allColumns.value.forEach(col => col.visible = true)
    }

    onMounted(() => {
        updateTableWidth()

        const tableEl = getTableElement()
        if (tableEl && window.ResizeObserver) {
            tableResizeObserver = new ResizeObserver(updateTableWidth)
            tableResizeObserver.observe(tableEl)
        }

        if (!tableKey) return
        window.addEventListener('resize', onResize);

        try {
            const allSaved = localStorage.getItem(STORAGE_KEY)
            if (allSaved) {
                const parsed = JSON.parse(allSaved)
                const savedMap = parsed[tableKey]
                if (savedMap && Array.isArray(savedMap)) {
                    allColumns.value.forEach(col => {
                        const s = savedMap.find(c => c.dataIndex === col.dataIndex)
                        if (s !== undefined) col.visible = s.visible
                    })
                }
            }
        } catch (e) {
            console.warn(`Load columns error: ${tableKey}`, e)
        }
    })

    onBeforeUnmount(() => {
        window.removeEventListener('resize', onResize)
        tableResizeObserver?.disconnect()
    })

    if (tableKey) {
        watch(
            () => allColumns.value.map(c => ({ dataIndex: c.dataIndex, visible: c.visible })),
            (newVal) => {
                try {
                    const allSaved = localStorage.getItem(STORAGE_KEY)
                    const parsed = allSaved ? JSON.parse(allSaved) : {}
                    parsed[tableKey] = newVal
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed))
                } catch (e) {
                    console.warn(`Save columns error: ${tableKey}`, e)
                }
            },
            { deep: true }
        )
    }

    return {
        columns,
        scrollX,
        scrollY,
        allColumns,
        tableRef,
        tableScroll,
        updateTableYScroll,
        toggleColumn,
        resetColumns,
        visibleColumns,
        renderSettingsDropdown
    }
}
